import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isExpaiApiEnabled } from '@/integrations/api/config';
import * as notificationsApi from '@/integrations/api/notifications_api';
import { useAuth } from '@/contexts/AuthContext';
import { getTransmitClient, type TransmitSubscription } from '@/integrations/api/transmit_client';

export function usePlatformNotifications() {
  const { user, useApiAuth } = useAuth();
  const enabled = useApiAuth && isExpaiApiEnabled() && !!user;

  const query = useQuery({
    queryKey: ['platform_notifications', user?.id],
    enabled,
    queryFn: () => notificationsApi.fetchNotifications(),
    refetchInterval: enabled ? 60_000 : false,
  });

  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled || !user?.id) return;
    let active = true;
    let sub: TransmitSubscription | null = null;

    void getTransmitClient().then((client) => {
      if (!active || !client) return;
      sub = client.subscription(`user/${user.id}/notifications`);
      return sub.create().then(() => {
        if (!active || !sub) return;
        sub.onMessage(() => {
          void qc.invalidateQueries({ queryKey: ['platform_notifications', user.id] });
        });
      });
    });

    return () => {
      active = false;
      void sub?.delete();
    };
  }, [enabled, user?.id, qc]);

  const unread = (query.data ?? []).filter((n) => !n.read_at).length;

  const markAllRead = useCallback(async () => {
    if (!enabled) return;
    await notificationsApi.markAllNotificationsRead();
    await qc.invalidateQueries({ queryKey: ['platform_notifications', user?.id] });
  }, [enabled, qc, user?.id]);

  const markRead = useCallback(
    async (id: string) => {
      if (!enabled) return;
      await notificationsApi.markNotificationRead(id);
      await qc.invalidateQueries({ queryKey: ['platform_notifications', user?.id] });
    },
    [enabled, qc, user?.id],
  );

  return {
    items: query.data ?? [],
    unread,
    isLoading: query.isLoading,
    markAllRead,
    markRead,
    enabled,
  };
}
